import React, { useState, useEffect } from 'react';
import { TextField, Label, Button, NavTab, NavTabItem, shapes, Tag } from '@cimpress/react-components';
import { useModal } from '../../../contexts/modal-context';
import { ShipmentGroupConfiguration, ShipmentGroupOptions } from '../../../models/shipment-group';
import { isUndefined } from 'util';
import ListGroup, { ListGroupItem } from '../../../components/ListGroup';
import DeleteButton from '../../../components/utils/DeleteButton';
import { getShipmentGroupByCarrierAccountKey } from '../../../utils/shipment-groups';
import { matchShipmentGroupConfiguration } from '../../../utils/shipment-group-configurations/match';
import { useShipmentGroupConfigurations } from '../../../store/hooks/useShipmentGroupConfigurations';
import { useInternalShipmentGroups } from '../../../store/hooks/useInternalShipmentGroups';
import ShipmentGroupConfigurationForm from '../../../components/forms/ShipmentGroupConfigurationForm';

// tslint:disable-next-line:import-name
import style from './index.module.css';

const Spinner = shapes.Spinner;

const debounce = <T extends any[]>(action: (...args: T) => void, ms: number) => {
  return (...args: T) => {
    const timeout = setTimeout(() => action(...args), ms);
    return () => clearTimeout(timeout);
  };
};

const sortCustomShipmentGroups = (groupA: ShipmentGroupConfiguration, groupB: ShipmentGroupConfiguration) => {
  if (groupA.name !== groupB.name) {
    return groupA.name.localeCompare(groupB.name);
  }
  if (groupA.tag === null) {
    return -1;
  }
  if (groupB.tag === null) {
    return 1;
  }
  return groupA.tag.localeCompare(groupB.tag);
};

const optionLabels: Record<ShipmentGroupOptions, string> = {
  [ShipmentGroupOptions.ALWAYS_HIDDEN]: 'Always hidden',
  [ShipmentGroupOptions.ALWAYS_VISIBLE]: 'Always visible',
  [ShipmentGroupOptions.DEFAULT]: '',
};

export default () => {
  const modal = useModal();
  const [search, setSearch] = useState('');

  const {
    getShipmentGroupConfigurations,
    shipmentGroupConfigurations,
    isShipmentGroupConfigurationsLoading,
    deleteShipmentGroupConfiguration,
  } = useShipmentGroupConfigurations();

  const {
    internalShipmentGroups,
    getInternalShipmentGroups,
    isInternalShipmentGroupsLoading,
  } = useInternalShipmentGroups();

  const [displayedShipmentGroupConfigurations, setDsplayedShipmentGroupConfigurations] =
    useState<ShipmentGroupConfiguration[]>([]);
  const [active, setActive] = useState<ShipmentGroupOptions>();
  const [[totalCount, visibleCount, hiddenCount], setCounts] = useState<[number, number, number]>([0, 0, 0]);

  const updateCounts = (shipmentGroupConfiguration: ShipmentGroupConfiguration) =>
    setCounts(([total, visible, hidden]) => [
      total + 1,
      visible + (shipmentGroupConfiguration.option === ShipmentGroupOptions.ALWAYS_VISIBLE ? 1 : 0),
      hidden + (shipmentGroupConfiguration.option === ShipmentGroupOptions.ALWAYS_HIDDEN ? 1 : 0),
    ]);

  const resetCounts = () => setCounts([0, 0, 0]);

  useEffect(
    () => {
      getShipmentGroupConfigurations();
      getInternalShipmentGroups();
    },
    [],
  );

  useEffect(
    debounce(
      () => {
        const searchRegExp = new RegExp(search, 'i');
        const matchingShipmentGroupConfigurations: ShipmentGroupConfiguration[] = [];
        resetCounts();
        for (const shipmentGroupConfiguration of Object.values(shipmentGroupConfigurations)) {
          if (search.length) {
            const matching = matchShipmentGroupConfiguration(
              shipmentGroupConfiguration,
              internalShipmentGroups,
              searchRegExp,
            );
            if (matching) {
              updateCounts(shipmentGroupConfiguration);
              matchingShipmentGroupConfigurations.push(shipmentGroupConfiguration);
            }
          } else {
            updateCounts(shipmentGroupConfiguration);
            matchingShipmentGroupConfigurations.push(shipmentGroupConfiguration);
          }
        }
        matchingShipmentGroupConfigurations.sort((a, b) => a.name.localeCompare(b.name));
        setDsplayedShipmentGroupConfigurations(matchingShipmentGroupConfigurations);
      },
      500,
    ),
    [shipmentGroupConfigurations, internalShipmentGroups, search],
  );

  const editCustomShipmentGroup = (shipmentGroupConfiguration: ShipmentGroupConfiguration) => () => {
    modal(
      'Edit Shipment Group',
      hide => <ShipmentGroupConfigurationForm close={hide} shipmentGroupConfiguration={shipmentGroupConfiguration} />,
      { bsStyle: 'info' },
    );
  };

  const content = () => {
    if (isInternalShipmentGroupsLoading || isShipmentGroupConfigurationsLoading) {
      return <Spinner />;
    }

    if (Object.values(shipmentGroupConfigurations).length === 0) {
      return <h6>
        Create your first shipment group.
      </h6>;
    }

    if (displayedShipmentGroupConfigurations.length === 0) {
      return <h6>
        No shipment groups found matching your search.
      </h6>;
    }

    return <ListGroup className={style.ShipmentGroupList}>
      {displayedShipmentGroupConfigurations
        .filter(customShipmentGroup => isUndefined(active) || customShipmentGroup.option === active)
        .sort(sortCustomShipmentGroups)
        .map(
          customShipmentGroup => <ListGroupItem key={customShipmentGroup.id}>
            <div
              className={style.ShipmentGroupButton}
              onClick={editCustomShipmentGroup(customShipmentGroup)}
            >
              <div className={style.ShipmentGroup}>
                <h5>
                  {customShipmentGroup.pinned &&
                    <i
                      className={`fas fa-thumbtack ${style.PinnedIcon}`}
                      title="Pinned to the top of the page"
                    />}
                  <span className={style.ShipmentGroupName}>{customShipmentGroup.name}</span>
                  {customShipmentGroup.option !== ShipmentGroupOptions.DEFAULT &&
                    <span className={style.ShipmentGroupOption}>
                      &nbsp;- {optionLabels[customShipmentGroup.option]}
                    </span>
                  }
                </h5>
                <div>
                  <p>
                    {customShipmentGroup.tag && <Label text={customShipmentGroup.tag} type="success"/>}
                    {customShipmentGroup.carrierAccountKeys
                      .map(carrierAccountKey =>
                        getShipmentGroupByCarrierAccountKey(internalShipmentGroups, carrierAccountKey))
                      .filter(shipmentGroup => !isUndefined(shipmentGroup))
                      .map(
                        shipmentGroup => <span>
                          <Tag value={shipmentGroup!.carrierAccountName} key={shipmentGroup!.carrierAccountKey}/>&nbsp;
                        </span>,
                      )}
                  </p>
                </div>
              </div>
              <DeleteButton onClick={() => deleteShipmentGroupConfiguration(customShipmentGroup)}>
                Do you want to delete the shipment group "<i>
                  {customShipmentGroup.name}
                  {customShipmentGroup.tag ? ` - ${customShipmentGroup.tag}` : ''}
                </i>"?
              </DeleteButton>
            </div>
          </ListGroupItem>,
      )}
    </ListGroup>;
  };

  return <>
    <div>
      <h3>Shipment Groups <Label text={`${displayedShipmentGroupConfigurations.length}`} /></h3>
      <Button
        type="primary"
        onClick={() => modal('Create Shipment Group', hide => <ShipmentGroupConfigurationForm close={hide} />, { bsStyle: 'info' })}
      >
        <i className="fa fa-plus" /> Create Shipment Group
      </Button>
    </div>
    <TextField
      label="Search"
      value={search}
      onChange={e => setSearch(e.target.value)}
    />
    <NavTab>
      <NavTabItem active={isUndefined(active)}>
        <button onClick={() => setActive(undefined)}>All ({totalCount})</button>
      </NavTabItem>
      <NavTabItem active={active === ShipmentGroupOptions.ALWAYS_VISIBLE}>
        <button
          onClick={() => setActive(ShipmentGroupOptions.ALWAYS_VISIBLE)}
        >
          Always visible ({visibleCount})
        </button>
      </NavTabItem>
      <NavTabItem active={active === ShipmentGroupOptions.ALWAYS_HIDDEN}>
        <button
          onClick={() => setActive(ShipmentGroupOptions.ALWAYS_HIDDEN)}
        >
          Always hidden ({hiddenCount})
        </button>
      </NavTabItem>
    </NavTab>
    <div>
      {content()}
    </div>
  </>;
};
